import {
    ManufacturerProductImageDetail,
    ManufacturerProductVideoDetail,
} from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { suffixDimension } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import ImageGallery from '@deltek/specification-client/core/Widgets/ImageGallery'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import VideoOverlay from 'pages/components/VideoOverlay'
import React, { useEffect, useState } from 'react'
import { DialogContext } from '../DialogContext'
import EmbeddedVideo from './EmbeddedVideo'

const useStyles = makeStyles((theme: Theme) => ({
    galleryContainer: {
        borderBottom: '1px solid #c9c9c9',
        width: '100%',
    },
    noImage: {
        height: 400,
        width: '100%',
        [theme.breakpoints.down('md')]: {
            width: 400,
            height: 386,
        },
    },
    tenMaxTotal: {
        color: '#fff',
        fontSize: pxToRem(24),
        fontWeight: 500,
    },
    '& .MuiSvgIcon-root .MuiSvgIcon-fontSizeLarge': {
        [theme.breakpoints.down('md')]: {
            fontSize: pxToRem(0.8),
        },
    },
    uploadButtonContainer: {
        marginTop: theme.spacing(3),
        '& .actionButtonContainer > button': {
            borderRadius: 50,
            fontSize: pxToRem(20),
            paddingLeft: theme.spacing(8),
            paddingRight: theme.spacing(8),
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
    },
    lightGrayBackground: {
        backgroundColor: '#c9c9c9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    noImageThumbText: {
        color: '#fff',
        fontWeight: 500,
        fontSize: pxToRem(12),
        [theme.breakpoints.down('md')]: {
            fontSize: pxToRem(13),
        },
    },
    largeFont: {
        fontSize: pxToRem(72),
        color: '#fff',
        fontWeight: 500,
        [theme.breakpoints.down('md')]: {
            fontSize: pxToRem(46),
        },
    },
    thumbnail: {
        border: 'none !important',
        textAlign: 'center',
        marginLeft: 0,
        display: 'inline-block',
        width: 100,
        background: 'transparent',
        marginBottom: theme.spacing(0.5),
        '& img': {
            height: 50,
            cursor: 'pointer',
            width: '75%',
            [theme.breakpoints.down('md')]: {
                height: 35,
                width: '60%',
            },
        },

        '&.active img': {
            height: 50,
            width: '85%',
            [theme.breakpoints.down('md')]: {
                height: 35,
                width: '70%',
            },
        },
        '&:focus': {
            outline: 'none',
        },
    },

    noImgThumbnail: {
        height: 60,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },

    activeImage: {
        borderBottom: `1px solid #c9c9c9`,
        width: 'inherit',
        height: 400,
        [theme.breakpoints.down('md')]: {
            height: 310,
        },
        [theme.breakpoints.between('xl', 'lg')]: {
            height: 385,
        },
    },
}))

const emptyPlaceholder = {
    empty: {
        // empty placeholder to show no image / video
        order: 0,
        imgUrl: '',
        thumbnailUrl: '',
    },
}
export type VideoSource = 'YouTube' | 'Vimeo'

export interface ListingDetailMediaCarouselProps {
    images?: Partial<ManufacturerProductImageDetail>[]
    videos?: Partial<ManufacturerProductVideoDetail>[]
    startIndex?: number
    searchLoading?: boolean
}
export interface ConsolidatedProductMedia {
    id?: string
    order: number
    imgUrl: string
    originalUrl?: string
    originalFilename?: string
    filename?: string
    thumbnailUrl: string
    fromSource?: VideoSource
    thumbnailUrlFetching?: boolean
    videoId?: string
    isPrimary?: boolean
    renderThumbInner?: () => void
    renderItem?: (item: any) => void
}

const ListingDetailMediaCarousel: React.FC<ListingDetailMediaCarouselProps> = ({
    images,
    videos,
    searchLoading,
}) => {
    const classes = useStyles()
    const { listingId } = React.useContext(DialogContext)
    const [consolidatedProductMedia, setConsolidatedProductMedia] = useState<
        Record<string, ConsolidatedProductMedia>
    >(emptyPlaceholder)
    const consolidatedMediaKeys = Object.keys(consolidatedProductMedia)
    const orderedConsolidatedMediaKeys = consolidatedMediaKeys.sort(
        (key1, key2) =>
            consolidatedProductMedia[key1].order -
            consolidatedProductMedia[key2].order
    )

    // Consolidates images and videos to ConsolidatedProductMedia
    useEffect(() => {
        // consolidate media
        const media: Record<string, ConsolidatedProductMedia> = {}
        images?.forEach((image) => {
            media[image.id!] = {
                id: image.id,
                order: image.order!,
                imgUrl: suffixDimension({
                    filename: image?.filename ?? '',
                    dimension: {
                        width: 800,
                        height: 600,
                    },
                }),
                thumbnailUrl: suffixDimension({
                    filename: image?.filename ?? '',
                    dimension: {
                        width: 400,
                        height: 300,
                    },
                }),
                originalUrl: image.filename,
                originalFilename:
                    image?.originalFilename ?? image?.filename ?? '',
            }
        })
        videos?.forEach((video) => {
            media[video.id!] = {
                id: video.id,
                order: video.order!,
                imgUrl: video?.videoStillImageUrl ?? '',
                thumbnailUrl: video?.videoStillImageUrl ?? '',
                fromSource: video?.fromSource as VideoSource,
                videoId: video?.videoId ?? '',
            }
        })
        setConsolidatedProductMedia(media)
    }, [images, videos])

    // Empty list
    useEffect(() => {
        if (Object.keys(consolidatedProductMedia).length === 0) {
            setConsolidatedProductMedia(emptyPlaceholder)
        }
    }, [consolidatedProductMedia])
    return (
        <div className={clsx(classes.galleryContainer, 'listing')}>
            <ImageGallery
                listingId={listingId}
                items={orderedConsolidatedMediaKeys.map((key) => ({
                    original: consolidatedProductMedia[key].imgUrl,
                    videoSource: consolidatedProductMedia[key].fromSource,
                    videoId: consolidatedProductMedia[key].videoId,
                    thumbnail: consolidatedProductMedia[key].imgUrl,
                    thumbnailClass: classes.thumbnail,

                    renderThumbInner: () => {
                        if (!consolidatedProductMedia[key].imgUrl) {
                            if (searchLoading) {
                                return (
                                    <Skeleton
                                        animation="pulse"
                                        variant="rect"
                                        width={50}
                                        className="loadingSkeleton"
                                    />
                                )
                            }
                            return (
                                <div
                                    id="thumbnailsWrapper"
                                    className={clsx(
                                        classes.noImgThumbnail,
                                        classes.lightGrayBackground
                                    )}
                                >
                                    <Typography
                                        className={classes.noImageThumbText}
                                        component="span"
                                    >
                                        No Image/Video
                                    </Typography>
                                </div>
                            )
                        }

                        const isVideo =
                            consolidatedProductMedia[key].videoId &&
                            consolidatedProductMedia[key].fromSource

                        if (!isVideo) {
                            return (
                                <img
                                    id="thumbnailImage"
                                    alt="Product listing preview thumbnail"
                                    src={
                                        consolidatedProductMedia[key]
                                            .thumbnailUrl
                                    }
                                />
                            )
                        }

                        return (
                            <VideoOverlay
                                src={consolidatedProductMedia[key].imgUrl}
                                className="listingPreviewThumbnail"
                            />
                        )
                    },
                    renderItem: (item: any) => {
                        if (!consolidatedProductMedia[key].imgUrl) {
                            if (searchLoading) {
                                return (
                                    <div>
                                        <Skeleton variant="text" />
                                        <Skeleton
                                            variant="circle"
                                            width={40}
                                            height={40}
                                        />
                                        <Skeleton
                                            variant="rect"
                                            width={'inherit'}
                                            height={300}
                                        />
                                    </div>
                                )
                            }
                            return (
                                <GridContainer
                                    justifyContent="center"
                                    className={clsx(
                                        classes.noImage,
                                        classes.lightGrayBackground
                                    )}
                                    direction="column"
                                >
                                    <GridItem>
                                        <Typography
                                            className={clsx(
                                                classes.tenMaxTotal,
                                                'productListingCardNoImageText'
                                            )}
                                            component="span"
                                        >
                                            No Image/Video
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            )
                        }
                        if (!consolidatedProductMedia[key].fromSource) {
                            return (
                                <>
                                    <img
                                        className={classes.activeImage}
                                        id="activeImage"
                                        alt="Product listing preview"
                                        src={item.original}
                                    />
                                </>
                            )
                        }
                        return (
                            <>
                                <EmbeddedVideo
                                    videoId={item.videoId}
                                    videoSource={item.videoSource}
                                    className={classes.activeImage}
                                />
                            </>
                        )
                    },
                }))}
            />
        </div>
    )
}

export default ListingDetailMediaCarousel
